"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import Link from "next/link";
import React, { useMemo } from "react";
import Slider from "react-slick";
import styled from "styled-components";
import { Text, TextSize, TextWeight } from "../../components";
import { useMatchBreakpoints } from "../../hooks";
// Types

import Arrow from "./arrow.png";

// Skeleton Component
export const HomepageSliderSkeleton = () => {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="w-full h-[400px] md:h-[710px] bg-gray-200 animate-pulse" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="mb-6 max-w-full"><div className="w-[300px] md:w-[500px] h-[100px] md:h-[150px] bg-gray-300 animate-pulse rounded" /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="absolute inset-0 flex flex-col justify-center items-center p-4 md:p-8">{t1}<div className="w-full max-w-4xl"><div className="flex justify-center gap-4 flex-wrap"><div className="w-32 h-12 bg-gray-300 animate-pulse rounded" /><div className="w-32 h-12 bg-gray-300 animate-pulse rounded" /></div></div></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="w-full h-full"><HomepageSliderWrapper><div className="relative w-full h-full">{t0}{t2}<div className="absolute bottom-[25px] left-0 right-0 flex justify-center gap-2 mt-4"><div className="w-3 h-3 bg-gray-300 animate-pulse rounded-full" /><div className="w-3 h-3 bg-gray-300 animate-pulse rounded-full" /><div className="w-3 h-3 bg-gray-300 animate-pulse rounded-full" /></div></div></HomepageSliderWrapper></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};
export const HomepageSlider = ({
  slides,
  isLoading
}) => {
  const {
    isXs,
    isSm,
    isMd
  } = useMatchBreakpoints();
  const isMedium = isMd;
  const isMobile = isXs || isSm;
  const baseUrl = useMemo(() => process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost/prestashop", []);

  // Memoize slider settings
  const sliderSettings = useMemo(() => ({
    dots: true,
    infinite: slides.length > 1,
    loop: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    prevArrow: <StyledImage src={Arrow} alt="Arrow" />,
    nextArrow: <StyledImage src={Arrow} alt="Arrow" />,
    responsive: [{
      breakpoint: 768,
      settings: {
        arrows: false,
        dots: true
      }
    }]
  }), []);

  // Memoize utility functions
  const getActiveButtons = useMemo(() => slide => {
    let count = 0;
    const buttonTexts = [slide.buttonText_1, slide.buttonText_2, slide.buttonText_3, slide.buttonText_4];
    buttonTexts.forEach(text => {
      if (text && text.trim() !== "") count++;
    });
    return count;
  }, []);
  const getImagePath = useMemo(() => (slide_0, isMobileParam) => {
    const imagePath = isMobileParam ? slide_0.imagePathMobile : slide_0.imagePathDesktop;
    return `${baseUrl}${imagePath}`;
  }, [baseUrl]);
  const getContentPath = useMemo(() => (slide_1, isMobileParam_0) => {
    const contentPath = isMobileParam_0 ? slide_1.contentPathMobile : slide_1.contentPathDesktop;
    return contentPath && contentPath.trim() !== "" ? `${baseUrl}${contentPath}` : null;
  }, [baseUrl]);
  const isInternalLink = useMemo(() => url => {
    if (!url) return false;
    return url.startsWith("/") || url.startsWith(baseUrl) || !url.includes("://");
  }, [baseUrl]);
  const normalizeUrl = useMemo(() => url_0 => {
    if (!url_0) return "#";
    if (url_0.startsWith("/")) return url_0;
    if (url_0.startsWith(baseUrl)) return url_0.replace(baseUrl, "/");
    return url_0;
  }, [baseUrl]);

  // Memoize processed slides data
  const processedSlides = useMemo(() => {
    return slides && slides.length > 0 ? slides.map((slide_2, index) => {
      const activeButtons = getActiveButtons(slide_2);
      const imagePath_0 = getImagePath(slide_2, isMobile || false);
      const contentPath_0 = getContentPath(slide_2, isMobile || false);
      const boxShift = isMobile ? parseInt(slide_2.boxShiftMobile) : parseInt(slide_2.boxShift);

      // Process button data
      const buttonData = [{
        text: slide_2.buttonText_1,
        url: slide_2.buttonUrl_1.replace("https://carry.pl", ""),
        backgroundColor: slide_2.buttonBackgroundColor_1,
        textColor: slide_2.buttonTextColor_1,
        borderColor: slide_2.buttonBorderColor_1
      }, {
        text: slide_2.buttonText_2,
        url: slide_2.buttonUrl_2.replace("https://carry.pl", ""),
        backgroundColor: slide_2.buttonBackgroundColor_2,
        textColor: slide_2.buttonTextColor_2,
        borderColor: slide_2.buttonBorderColor_2
      }, {
        text: slide_2.buttonText_3,
        url: slide_2.buttonUrl_3.replace("https://carry.pl", ""),
        backgroundColor: slide_2.buttonBackgroundColor_3,
        textColor: slide_2.buttonTextColor_3,
        borderColor: slide_2.buttonBorderColor_3
      }, {
        text: slide_2.buttonText_4,
        url: slide_2.buttonUrl_4.replace("https://carry.pl", ""),
        backgroundColor: slide_2.buttonBackgroundColor_4,
        textColor: slide_2.buttonTextColor_4,
        borderColor: slide_2.buttonBorderColor_4
      }];
      const activeButtonsData = buttonData.filter(button => button.text && button.text.trim() !== "");
      return {
        index,
        slide: slide_2,
        activeButtons,
        imagePath: imagePath_0,
        contentPath: contentPath_0,
        boxShift,
        activeButtonsData
      };
    }) : [];
  }, [slides, isMobile, getActiveButtons, getImagePath, getContentPath]);
  const renderButtons = useMemo(() => {
    const renderButtonsFunction = (activeButtonsData_0, activeButtons_0, isMd_0, isSm_0) => {
      const buttonClassName = "px-4 lg:px-8 py-2 lg:py-3 text-center rounded transition-all duration-300 hover:opacity-80 font-medium";
      const createButton = (button_0, index_0) => {
        const buttonStyle = {
          backgroundColor: button_0.backgroundColor,
          color: button_0.textColor,
          border: `1px solid ${button_0.borderColor}`,
          borderRadius: 0
        };
        const textProps = {
          text: button_0.text,
          textTransform: "uppercase",
          className: "tracking-widest",
          weight: TextWeight.BOLD,
          fontSize: isMobile ? TextSize.XXS : TextSize.S
        };
        if (isInternalLink(button_0.url)) {
          return <Link key={index_0} href={normalizeUrl(button_0.url)} className={buttonClassName} style={buttonStyle}>
              <Text {...textProps} />
            </Link>;
        }
        return <a key={index_0} href={button_0.url} className={buttonClassName} style={buttonStyle} target="_blank" rel="noopener noreferrer">
            <Text {...textProps} fontSize={TextSize.S} />
          </a>;
      };
      const buttons = activeButtonsData_0.map(createButton);

      // Każdy button w osobnym rzędzie dla bardzo małych ekranów
      if (isSm_0) {
        return <div className="flex flex-col gap-4 w-full">{buttons}</div>;
      }

      // Specjalna logika dla mobile z więcej niż 2 buttonami
      if (isMd_0 && activeButtons_0 > 2) {
        return <div className="grid grid-cols-2 gap-4 w-full">
            {buttons.slice(0, 2)}
            <div className="col-span-2 flex justify-center">
              {buttons.slice(2, 3)}
            </div>
            {buttons.slice(3).map((button_1, index_1) => <div key={`extra-${index_1}`} className={index_1 % 2 === 0 ? "col-span-2 flex justify-center" : ""}>
                {button_1}
              </div>)}
          </div>;
      }
      return <div className="flex justify-evenly xxl:justify-between gap-4 flex-wrap">
          {buttons}
        </div>;
    };
    renderButtonsFunction.displayName = "renderButtons";
    return renderButtonsFunction;
  }, [isInternalLink, normalizeUrl]);
  if (isLoading) return <HomepageSliderSkeleton />;
  if (slides.length === 0) return null;
  return <div className="w-full">
      <HomepageSliderWrapper>
        <Slider {...sliderSettings}>
          {processedSlides.map(({
          index: index_2,
          slide: slide_3,
          activeButtons: activeButtons_1,
          imagePath: imagePath_1,
          contentPath: contentPath_1,
          boxShift: boxShift_0,
          activeButtonsData: activeButtonsData_1
        }) => <div key={index_2} className="relative w-full max-h-[710px]">
                <StyledImage className="w-full h-auto object-cover" src={imagePath_1} alt={`Slide ${index_2 + 1}`} width={1920} height={800} priority={index_2 === 0} // Load first image with priority
          sizes="100vw" />

                <div className="absolute left-0 right-0 w-[40%] mx-auto inset-0 flex flex-col justify-center items-center p-4 md:p-8" style={boxShift_0 ? {
            marginTop: `${boxShift_0}px`
          } : {}}>
                  {contentPath_1 && <div className="mb-6 max-w-full relative">
                      <StyledImage className="h-auto" style={{
                width: `${slide_3.widthRatio || "100"}%`
              }} src={contentPath_1} alt="Content" width={800} height={400} sizes="(max-width: 768px) 100vw, 800px" />
                    </div>}

                  <div className="w-full max-w-4xl">
                    {renderButtons(activeButtonsData_1, activeButtons_1, isMedium || false, isMobile || false)}
                  </div>
                </div>
              </div>)}
        </Slider>
      </HomepageSliderWrapper>
    </div>;
};
const StyledImage = styled(Image).withConfig({
  shouldForwardProp: prop => {
    return !["currentSlide", "slideCount"].includes(prop);
  }
})``;
const HomepageSliderWrapper = styled.div`
  max-height: 710px;
  width: 100%;
  overflow: hidden;
  max-width: 100vw;

  ${({
  theme
}) => theme.mediaQueries["2xl"]} {
    width: calc(100vw - 12px);
  }

  .slick-list {
    max-height: 710px;
  }

  .slick-track {
    max-height: 710px;

    .slick-slide {
      > div {
        max-height: 710px;
      }
    }
  }

  .slick-dots {
    bottom: 20px;

    li {
      button:before {
        font-size: 30px;
        color: ${({
  theme
}) => theme.colors.BLACK};
      }
    }
  }

  .slick-next,
  .slick-prev {
    position: absolute;
    z-index: 1;
    width: 33px;
    height: 64px;
  }

  .slick-next {
    right: 20px;
    transform: translateY(calc(50% - 64px));
  }

  .slick-prev {
    left: 20px;
    transform: translateY(calc(50% - 64px)) rotate(180deg);
  }
`;
export default HomepageSlider;